import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Fab,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  InputAdornment,
  CircularProgress,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useAuth } from "../contexts/AuthContext";
import { useOffline } from "../contexts/OfflineContext";
import { apiGet, apiPost, apiPut, apiDelete } from "../utils/apiClient";
import { HEADER_GRADIENT, HEADER_TEXT } from "../App";
import PullToRefresh from "../components/PullToRefresh";
import useAutoRefresh from "../hooks/useAutoRefresh";

export default function TripList() {
  const navigate = useNavigate();
  const { user, logout, isAdmin, setAdminMode } = useAuth();
  const { refreshCount } = useOffline();
  const [tab, setTab] = useState(0);
  const [trips, setTrips] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTrip, setNewTrip] = useState({ name: "", description: "", currency: "EUR" });
  const [anchorEl, setAnchorEl] = useState(null);

  // Join by code state
  const [joinCode, setJoinCode] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState("");

  const loadTrips = async () => {
    try {
      const data = await apiGet("/api/trips");
      setTrips(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { loadTrips(); }, []);
  useAutoRefresh(loadTrips, 30000);

  const myTrips = trips.filter((t) => t.my_status === "approved");
  const pendingTrips = trips.filter((t) => t.my_status === "pending");

  const [cancelConfirmId, setCancelConfirmId] = useState(null);

  const handleCancelRequest = async () => {
    await apiDelete(`/api/trips/${cancelConfirmId}/apply`);
    await refreshCount();
    setTrips((prev) => prev.filter((t) => t.id !== cancelConfirmId));
    setCancelConfirmId(null);
  };

  const handleToggleStar = async (tripId) => {
    const { starred } = await apiPut(`/api/trips/${tripId}/star`);
    setTrips((prev) => prev.map((t) => t.id === tripId ? { ...t, starred } : t));
  };

  const handleCreate = async () => {
    const trip = await apiPost("/api/trips", newTrip);
    await refreshCount();
    setCreateOpen(false);
    setNewTrip({ name: "", description: "", currency: "EUR" });
    navigate(`/trips/${trip.id}`);
  };

  const handleJoinByCode = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!/^[A-Z]{4}$/.test(code)) {
      setJoinError("Please enter a valid 4-letter code.");
      return;
    }
    setJoinLoading(true);
    setJoinError("");
    try {
      navigate(`/${code}`);
    } finally {
      setJoinLoading(false);
    }
  };

  const renderTrip = (trip) => {
    const isPending = trip.my_status === "pending";
    return (
    <Card key={trip.id} sx={{ mb: 1.5, opacity: isPending ? 0.75 : 1 }}>
      <CardActionArea
        onClick={() => !isPending && navigate(`/trips/${trip.id}`)}
        sx={{ borderRadius: 24, cursor: isPending ? "default" : "pointer" }}
      >
        <CardContent sx={{ p: 3, pb: "20px !important" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Typography fontWeight={700} fontSize="1.05rem" noWrap color="text.primary" sx={{ flex: 1, mr: 1 }}>
                  {trip.name}
                </Typography>
                {parseFloat(trip.total_spent) > 0 && (
                  <Typography fontWeight={800} fontSize="1.05rem" color="#2D3436" sx={{ whiteSpace: "nowrap", mr: 0.5 }}>
                    {parseFloat(trip.total_spent).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {trip.currency}
                  </Typography>
                )}
                {!isPending && (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleToggleStar(trip.id); }}
                    sx={{ p: 0.25, ml: 0.25, color: trip.starred ? "#FFB300" : "text.disabled" }}
                  >
                    {trip.starred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                  </IconButton>
                )}
              </Box>
              {trip.description && (
                <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                  {trip.description}
                </Typography>
              )}
              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={`${trip.traveler_count} ${Number(trip.traveler_count) === 1 ? "Traveler" : "Travelers"}`}
                  size="small"
                  sx={{ bgcolor: "rgba(168,216,234,0.3)", color: "text.secondary", height: 24, fontSize: "0.7rem" }}
                />
                {!isPending && trip.created_by === user?.id && (
                  <Chip label="Owner" size="small"
                    sx={{ bgcolor: "rgba(168,216,234,0.5)", color: "#2D3436", height: 24, fontSize: "0.7rem", fontWeight: 700 }} />
                )}
                {isPending && (
                  <>
                    <Chip label="⏳ Awaiting approval" size="small"
                      sx={{ bgcolor: "rgba(252,227,138,0.6)", color: "#7a6200", height: 24, fontSize: "0.7rem", fontWeight: 700 }} />
                    <Button size="small" onClick={(e) => { e.stopPropagation(); setCancelConfirmId(trip.id); }}
                      sx={{ color: "#FFAAA5", fontWeight: 700, fontSize: "0.7rem", minWidth: 0, height: 24, py: 0, px: 1, ml: "auto" }}>
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
    );
  };

  const allMyTrips = [
    ...myTrips.sort((a, b) => (b.starred ? 1 : 0) - (a.starred ? 1 : 0)),
    ...pendingTrips,
  ];

  return (
    <Box sx={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ background: HEADER_GRADIENT, px: 2, pt: 2, pb: 0, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography variant="h6" fontWeight={700} color={HEADER_TEXT}>
            ✈️ TravelSplit
          </Typography>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
            {user?.avatar_url
              ? <Avatar src={user.avatar_url} sx={{ width: 36, height: 36, border: "2px solid rgba(255,255,255,0.8)" }} />
              : <AccountCircleIcon sx={{ color: HEADER_TEXT }} />
            }
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled><Typography variant="body2">{user?.name}</Typography></MenuItem>
            {isAdmin && (
              <MenuItem onClick={() => { setAnchorEl(null); setAdminMode(true); navigate("/admin"); }}>
                Enter Admin Mode
              </MenuItem>
            )}
<MenuItem onClick={() => { setAnchorEl(null); logout(); }}>Sign out</MenuItem>
          </Menu>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            "& .MuiTabs-flexContainer": { gap: 1 },
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              borderRadius: "20px",
              minHeight: 36,
              px: 2.5,
              py: 0.5,
              color: "rgba(45,52,54,0.45)",
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: 0.5,
              textTransform: "uppercase",
              fontFamily: "'Quicksand', sans-serif",
            },
            "& .Mui-selected": {
              bgcolor: "rgba(255,255,255,0.92)",
              color: "#1a1a1a",
              fontWeight: 800,
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            },
          }}
        >
          <Tab label="My Trips" />
          <Tab label="Join Trip" />
        </Tabs>
      </Box>

      <PullToRefresh onRefresh={loadTrips} sx={{ flex: 1, overflowY: "auto", overscrollBehavior: "contain" }}>
      <Container maxWidth="sm" sx={{ py: 2.5, pb: 12 }}>
        {/* My Trips tab */}
        {tab === 0 && (
          allMyTrips.length === 0
            ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 6, gap: 3 }}>
                <Typography fontSize="3rem">✈️</Typography>
                <Typography fontWeight={700} fontSize="1.2rem" color="text.primary" textAlign="center">
                  No trips yet
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 280 }}>
                  Create your first trip or join one with a code from a friend
                </Typography>
                <Stack spacing={1.5} sx={{ width: "100%", maxWidth: 280 }}>
                  <Button variant="contained" fullWidth onClick={() => setCreateOpen(true)}>
                    ✈️ Create Your First Trip
                  </Button>
                  <Button variant="outlined" fullWidth onClick={() => setTab(1)}>
                    🔑 Join a Trip
                  </Button>
                </Stack>
              </Box>
            )
            : allMyTrips.map((t) => renderTrip(t))
        )}

        {/* Join Trip tab */}
        {tab === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 4, gap: 2 }}>
            <Typography fontSize="3rem">🔑</Typography>
            <Typography fontWeight={700} fontSize="1.2rem" color="text.primary" textAlign="center">
              Join a Trip
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 300 }}>
              Enter the 4-letter code shared by the trip owner
            </Typography>

            <TextField
              placeholder="ABCD"
              value={joinCode}
              onChange={(e) => {
                setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4));
                setJoinError("");
              }}
              inputProps={{
                maxLength: 4,
                style: {
                  textAlign: "center",
                  fontSize: "2rem",
                  fontWeight: 800,
                  letterSpacing: "0.5em",
                  fontFamily: "'Quicksand', monospace",
                  textTransform: "uppercase",
                },
              }}
              error={Boolean(joinError)}
              helperText={joinError}
              sx={{ width: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              size="large"
              onClick={handleJoinByCode}
              disabled={joinCode.length !== 4 || joinLoading}
              sx={{ px: 5 }}
            >
              {joinLoading ? <CircularProgress size={22} /> : "Continue"}
            </Button>
          </Box>
        )}
      </Container>
      </PullToRefresh>

      <Fab color="primary" sx={{ position: "fixed", bottom: 32, right: 24, zIndex: 1100 }} onClick={() => setCreateOpen(true)}>
        <AddIcon sx={{ color: HEADER_TEXT }} />
      </Fab>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>New Trip ✈️</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1.5 }}>
            <TextField label="Trip name" fullWidth value={newTrip.name}
              onChange={(e) => setNewTrip({ ...newTrip, name: e.target.value })} />
            <TextField label="Description (optional)" fullWidth multiline rows={2}
              value={newTrip.description} onChange={(e) => setNewTrip({ ...newTrip, description: e.target.value })} />
            <TextField label="Currency" fullWidth value={newTrip.currency}
              onChange={(e) => setNewTrip({ ...newTrip, currency: e.target.value })}
              inputProps={{ maxLength: 10 }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!newTrip.name.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(cancelConfirmId)} onClose={() => setCancelConfirmId(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Request?</DialogTitle>
        <DialogContent>
          <Typography>Your join request will be withdrawn. You can request to join again later.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCancelConfirmId(null)} sx={{ color: "text.secondary" }}>Keep</Button>
          <Button variant="contained" color="error" onClick={handleCancelRequest}>Cancel Request</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
